import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Printer, Receipt, Briefcase, GraduationCap, Stethoscope, Scales, Ticket } from "@phosphor-icons/react";

export default function MyPrints() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [applications, setApplications] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [doctorConsults, setDoctorConsults] = useState([]);
  const [legalConsults, setLegalConsults] = useState([]);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    if (!user || user === false) return;
    (async () => {
      try { const { data } = await api.get("/orders/mine"); setOrders(data); } catch {}
      try { const { data } = await api.get("/jobs/applications/mine"); setApplications(data); } catch {}
      try { const { data } = await api.get("/training/certificates"); setCertificates(data); } catch {}
      try { const { data } = await api.get("/consultations/mine"); setDoctorConsults((data || []).filter((c) => c.patient_id === user.id)); } catch {}
      try { const { data } = await api.get("/legal-consultations/mine"); setLegalConsults((data || []).filter((c) => c.client_id === user.id)); } catch {}
      try { const { data } = await api.get("/events/rsvps/mine"); setTickets(data); } catch {}
    })();
  }, [user]);

  if (!user || user === false) {
    return <div className="max-w-7xl mx-auto px-6 py-12 samaaj-card p-10 text-center text-[#5C564D]">Please log in to see your printable records.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]">My prints</div>
      <h1 className="font-heading text-5xl mt-2">Everything worth <em className="not-italic text-[#2C4A3B]">keeping</em>.</h1>
      <p className="text-[#5C564D] mt-2 max-w-2xl">Orders, applications, certificates, doctor and lawyer consults, and event passes — printable in one place.</p>

      <Section title="Items ordered" icon={Receipt} empty="No orders yet." items={orders}
        render={(o) => (
          <>
            <div className="font-semibold">{o.listing_title || "Order"} · ${o.amount_total ? (o.amount_total / 100).toFixed(2) : o.amount || 0}</div>
            <div className="text-xs text-[#5C564D]">{o.payment_status || o.status} · {new Date(o.created_at).toLocaleDateString()}</div>
          </>
        )}
        actionsFor={(o) => <Link to={`/prints/order/${o.id}`} target="_blank" className="samaaj-btn-outline text-xs flex items-center gap-1"><Printer size={12} /> Print</Link>}
      />

      <Section title="Jobs applied" icon={Briefcase} empty="No job applications yet." items={applications}
        render={(a) => (
          <>
            <div className="font-semibold">{a.job_title}</div>
            <div className="text-xs text-[#5C564D]">{a.is_qualified ? "Qualified" : "Under review"} · {new Date(a.created_at).toLocaleDateString()}</div>
          </>
        )}
        actionsFor={(a) => <Link to={`/prints/application/${a.id}`} target="_blank" className="samaaj-btn-outline text-xs flex items-center gap-1"><Printer size={12} /> Print</Link>}
      />

      <Section title="Certificates & notes" icon={GraduationCap} empty="Complete a training program to earn a certificate." items={certificates}
        render={(c) => (
          <>
            <div className="font-semibold">{c.program_title}</div>
            <div className="text-xs text-[#5C564D]">Certificate {c.certificate_no} · {c.issued_at ? new Date(c.issued_at).toLocaleDateString() : ""}</div>
          </>
        )}
        actionsFor={(c) => <Link to={`/prints/certificate/${c.id}`} target="_blank" className="samaaj-btn-outline text-xs flex items-center gap-1"><Printer size={12} /> Print</Link>}
      />

      <Section title="Doctor consultations" icon={Stethoscope} empty="No doctor consultations yet." items={doctorConsults}
        render={(c) => (
          <>
            <div className="font-semibold">{c.title}</div>
            <div className="text-xs text-[#5C564D]">{c.body_system} · {(c.messages || []).length} messages</div>
          </>
        )}
        actionsFor={(c) => <Link to={`/prints/consult/${c.id}`} target="_blank" className="samaaj-btn-outline text-xs flex items-center gap-1"><Printer size={12} /> Print</Link>}
      />

      <Section title="Lawyer consultations" icon={Scales} empty="No legal consultations yet." items={legalConsults}
        render={(c) => (
          <>
            <div className="font-semibold">{c.title}</div>
            <div className="text-xs text-[#5C564D]">{c.category} · {(c.messages || []).length} messages</div>
          </>
        )}
        actionsFor={(c) => <Link to={`/prints/legal/${c.id}`} target="_blank" className="samaaj-btn-outline text-xs flex items-center gap-1"><Printer size={12} /> Print</Link>}
      />

      <Section title="Event tickets" icon={Ticket} empty="No event tickets yet." items={tickets}
        render={(t) => (
          <>
            <div className="font-semibold">{t.event_title} · {t.ticket_number}</div>
            <div className="text-xs text-[#5C564D]">{new Date(t.event_date).toLocaleString()} · {t.event_venue}</div>
          </>
        )}
        actionsFor={(t) => <Link to={`/events/ticket/${t.id}/print`} target="_blank" className="samaaj-btn-outline text-xs flex items-center gap-1"><Printer size={12} /> Print</Link>}
      />
    </div>
  );
}

function Section({ title, icon: Icon, empty, items, render, actionsFor }) {
  return (
    <section className="mt-10">
      <div className="flex items-center gap-2">
        <Icon size={20} weight="duotone" className="text-[#2C4A3B]" />
        <h2 className="font-heading text-2xl">{title}</h2>
        <span className="text-xs text-[#5C564D]">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <div className="mt-2 samaaj-card p-6 text-sm text-[#5C564D]">{empty}</div>
      ) : (
        <ul className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => (
            <li key={it.id} className="samaaj-card p-4 flex items-start justify-between gap-3" data-testid={`print-item-${it.id}`}>
              <div className="min-w-0">{render(it)}</div>
              <div className="shrink-0">{actionsFor(it)}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
